<template>
  <div style="width: 100%" class="pb-8 pt-4">
    <div class="d-flex align-center justify-center px-4" style="width: 100%">
      <v-autocomplete
        v-model="selected"
        :items="nodeCompares"
        item-text="name"
        item-value="nodeCompareId"
        :label="$t('nodeCompare.compareForm.select')"
        prepend-icon="mdi-view-dashboard"
        dense
        @change="(v) => change(v)"
      >
        <template v-slot:selection="data">
          <template v-if="typeof data.item !== 'object'">
            <v-chip v-text="data.item" small></v-chip>
          </template>
          <template v-else>
            <v-chip>
              <div v-html="data.item.name"></div>
            </v-chip>
          </template>
        </template>

        <template v-slot:item="data">
          <template v-if="typeof data.item !== 'object'">
            <v-list-item-content v-text="data.item"></v-list-item-content>
          </template>
          <template v-else>
            <v-list-item-content>
              <v-list-item-title v-html="data.item.name"></v-list-item-title>
              <v-list-item-subtitle
                v-html="data.item.description"
              ></v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action v-if="permitted('Dashboard.Delete')">
              <v-btn icon x-small @click.stop="deleteConfirm(data.item)">
                <v-icon color="error">mdi-delete</v-icon>
              </v-btn>
            </v-list-item-action>
          </template>
        </template>
      </v-autocomplete>

      <v-tooltip left v-if="permitted('Dashboard.Create')">
        <template v-slot:activator="{ on }">
          <v-btn icon @click="toggleDialog" color="success" v-on="on">
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("nodeCompare.compareForm.createTooltip") }}</span>
      </v-tooltip>
    </div>

    <v-dialog v-model="createDialog" max-width="450">
      <v-card>
        <v-form
          :lazy-validation="true"
          @submit.prevent="handleSubmit"
          v-model="valid"
          ref="form"
        >
          <v-card-title>
            {{ $t("nodeCompare.compareForm.createTitle") }}
          </v-card-title>

          <v-card-text>
            <v-text-field
              :label="$t('nodeCompare.compareForm.createName')"
              type="text"
              v-model="viewName"
              :rules="nameRules"
            />
            <v-text-field
              :label="$t('nodeCompare.compareForm.createDescription')"
              type="text"
              v-model="viewDescription"
            />
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn text small color="gray" @click="createDialog = false">{{
              $t("common.close")
            }}</v-btn>
            <v-btn text small color="primary" type="submit">{{
              $t("common.create")
            }}</v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <delete-dialog
      :dialog="confirmDelete"
      v-on:confirm-delete="remove"
      v-on:cancel-dialog="confirmDelete = false"
    />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import nodeCompareRepository from "@/api/repositories/nodeCompareRepository";
import DeleteDialog from "@/components/common/DeleteDialog";

export default {
  name: "NodeCompareForm",
  components: {
    DeleteDialog,
  },

  props: {
    value: {
      type: Object,
      default: null,
    },
  },

  model: { prop: "value", event: "input" },

  data() {
    return {
      selected: null,
      deleteId: null,
      confirmDelete: false,
      createDialog: false,
      valid: false,
      viewName: "",
      viewDescription: "",
      nodeCompares: [],

      nameRules: [(v) => !!v || this.$t("nodeCompare.validation.nameRequired")],
    };
  },

  methods: {
    ...mapActions("alert", ["success", "error"]),

    async remove() {
      this.confirmDelete = false;
      if (this.deleteId === null) return;

      await nodeCompareRepository.delete(this.deleteId);
      await this.load();
      this.deleteId = null;
    },

    deleteConfirm(item) {
      if (item.nodeCompareId === undefined) return;
      this.deleteId = item.nodeCompareId;
      this.confirmDelete = true;
    },

    toggleDialog() {
      this.createDialog = true;
    },

    async handleSubmit() {
      if (!this.$refs.form.validate()) {
        this.valid = false;
        return;
      }
      if (!(this.value?.nodes?.length > 0)) {
        this.error(this.$t("nodeCompare.validation.noSelectedKeys"));
        return;
      }

      const timerange = this.getTimeRangeAsDates(this.value?.chartDateRange);
      const payload = {
        name: this.viewName,
        description: this.viewDescription,
        nodes: this.value?.nodes,
        rangeStart: this.value?.chartDateRange?.start ?? "",
        rangeEnd: this.value?.chartDateRange?.end ?? "",
        initialStart:
          this.value?.chartDateRange?.start !== undefined
            ? this.toApiDate(timerange.start)
            : undefined,
        initialEnd:
          this.value?.chartDateRange?.end !== undefined
            ? this.toApiDate(timerange.end)
            : undefined,
        aggregationOptions: JSON.stringify(
          this.value?.chartAggregationSettings ?? {}
        ),
        treeFilterOptions: JSON.stringify(
          this.value?.treeFilterOptions ?? {
            applications: [],
            nodeTypes: [],
            decoders: [],
          }
        ),
      };

      const result = await nodeCompareRepository.create(payload);
      if (result.status === 200) {
        this.createDialog = false;
        this.success(this.$t("common.created"));
        await this.load();
        return;
      }
      this.error(this.$t("common.error"));
    },

    change(nodeCompareId) {
      const selected = this.nodeCompares.find(
        (x) => x.nodeCompareId.toString() == nodeCompareId
      );
      if (selected == undefined) return;

      const result = {
        chartDateRange: { start: selected.rangeStart, end: selected.rangeEnd },
        chartAggregationSettings: JSON.parse(selected.aggregationOptions),
        treeFilterOptions: JSON.parse(selected.treeFilterOptions),
        nodes: selected.nodes,
      };
      this.$emit("input", result);
    },

    async load() {
      let result = await nodeCompareRepository.get();
      if (result.status === 200) this.nodeCompares = result.data ?? [];
    },
  },

  async created() {
    this.selected = this.value;
    await this.load();
  },
};
</script>
